import Cookies from 'js-cookie';

import { useApplication } from '@bonusly/application';

import { HttpError } from '../../http';
import { CSRF_COOKIE_ID } from '../config/csrf';
import { useIdentityStore } from '../lib/store';
import { isExpired } from '../utils/token';
import { joinPath } from '../utils/url';

// Debug logging helper - can be enabled via localStorage.debugToken = 'true' in console
const debugLog = (...args: any[]) => {
	if (localStorage.getItem('debugToken') === 'true') {
		console.log('WIDGET DEBUG:', ...args);
	}
};

// Cookie name for persisting JWT across iframe reloads
// CRITICAL: Only used for widget iframe authentication (JWT-in-URL flows)
// NOT used for session-based authentication (direct navigation, spoofing)
const JWT_COOKIE_NAME = 'bonusly_widget_jwt';

// Safely detect iframe context without throwing SecurityError in cross-origin iframes
const isInIframe = (): boolean => {
	try {
		return window.self !== window.top;
	} catch (e) {
		// SecurityError in cross-origin iframes - assume we're in iframe
		return true;
	}
};

// Save JWT to cookie for persistence across iframe reloads
// Uses SameSite=None;Secure to work in cross-origin iframe contexts
// ONLY call this for widget iframe authentication (URL-based JWTs)
// NEVER call this for session-based JWTs (which can change due to spoofing/logout)
export const saveTokenToStorage = (jwt: string): void => {
	try {
		const cookieOptions = {
			sameSite: 'None' as const,
			secure: true,
			// Set expiration to match JWT expiration (will be validated on use)
			expires: 7, // 7 days
		};

		Cookies.set(JWT_COOKIE_NAME, jwt, cookieOptions);

		debugLog('JWT saved to cookie', { jwtLength: jwt.length });
	} catch (e) {
		console.error('WIDGET JWT COOKIE: Failed to save', e);
		debugLog('Failed to save JWT to cookie', e);
	}
};

// Load JWT from cookie
const getTokenFromStorage = (): string | undefined => {
	try {
		const jwt = Cookies.get(JWT_COOKIE_NAME);
		if (jwt) {
			debugLog('JWT loaded from cookie', { jwtLength: jwt.length });
			return jwt;
		}
		debugLog('No cookie found');
	} catch (e) {
		console.error('WIDGET JWT COOKIE: Failed to load', e);
		debugLog('Failed to load JWT from cookie', e);
	}
	return undefined;
};

// Clear JWT from cookie
export const clearTokenFromStorage = (): void => {
	try {
		// Must specify same options as when cookie was set for reliable removal
		Cookies.remove(JWT_COOKIE_NAME, {
			sameSite: 'None',
			secure: true,
		});
		debugLog('JWT cleared from cookie');
	} catch (e) {
		console.error('WIDGET JWT COOKIE: Failed to clear', e);
		debugLog('Failed to clear JWT from cookie', e);
	}
};

// Extract JWT token from URL parameters (for iframe widget authentication)
const getTokenFromUrl = (): string | undefined => {
	const urlParams = new URLSearchParams(window.location.search);
	const jwtFromUrl = urlParams.get('jwt');

	debugLog('getTokenFromUrl called', {
		currentUrl: window.location.href,
		hasJwtParam: !!jwtFromUrl,
		jwtLength: jwtFromUrl ? jwtFromUrl.length : 0,
	});

	if (jwtFromUrl) {
		debugLog('JWT found in URL, cleaning up', { jwtStart: jwtFromUrl.substring(0, 20) });
		// Clean the URL to remove the JWT parameter for security
		const cleanUrl = new URL(window.location.href);
		cleanUrl.searchParams.delete('jwt');
		window.history.replaceState({}, document.title, cleanUrl.toString());
		debugLog('URL cleaned', { newUrl: cleanUrl.toString() });

		// Save to cookie for persistence across reloads (works in cross-origin iframes)
		// This is ONLY for widget iframe authentication (JWT-in-URL flows)
		saveTokenToStorage(jwtFromUrl);

		return jwtFromUrl;
	}

	debugLog('No JWT found in URL');
	return undefined;
};

// Store JWT from URL to be used later
let urlJwtCache: string | undefined = undefined;

// Check for JWT in URL immediately when module loads (before any other token operations)
const initializeTokenFromUrl = () => {
	debugLog('initializeTokenFromUrl called');
	const urlJwt = getTokenFromUrl();
	if (urlJwt) {
		debugLog('URL JWT cached for later use', { jwtLength: urlJwt.length });
		urlJwtCache = urlJwt;
		return true;
	}
	debugLog('No URL JWT to cache');
	return false;
};

// Initialize JWT from URL when module loads
initializeTokenFromUrl();

export const getToken = async () => {
	debugLog('getToken called');
	// First check if we have a cached URL JWT to use
	if (urlJwtCache) {
		debugLog('using cached URL JWT', { jwtLength: urlJwtCache.length });
		useIdentityStore.setState({ token: urlJwtCache });
		const token = urlJwtCache;
		urlJwtCache = undefined; // Clear cache after use
		return token;
	}

	// Check in-memory store first
	let token = useIdentityStore.getState().token;
	debugLog('checking existing token in store', { exists: !!token, expired: token ? isExpired(token) : null });

	// If no token in memory OR in-memory token is expired, check cookie (for iframe reload persistence)
	// CRITICAL: Only use cached JWT if we're in an iframe context
	// This prevents cached JWTs from interfering with session-based auth
	const needsToken = !token || isExpired(token);

	if (needsToken && isInIframe()) {
		const storedToken = getTokenFromStorage();
		if (storedToken) {
			debugLog('found token in cookie, checking expiration');
			// Validate the stored token
			if (!isExpired(storedToken)) {
				useIdentityStore.setState({ token: storedToken });
				// Update local variable so the return logic below uses the loaded token
				token = storedToken;
				debugLog('using stored token from cookie (iframe context)');
			} else {
				debugLog('stored token is expired, clearing');
				clearTokenFromStorage();
			}
		}
	} else if (!isInIframe()) {
		debugLog('not in iframe context, skipping cookie check');
	}

	// When the application loads we don't have a token set. However, if there's a loader in the router
	// and the user is on that route it's going to attempt to make any async requests automatically before the
	// component itself is even mounted. This is almost guaranteed to happen *before* the token is even fetched.
	// Loaders use the globally available HTTP clients, each of which use this `async getToken()` function to
	// retrieve the auth token and add it to the request headers. The below check ensures any of those requests
	// will wait for the token to be fetched before proceeding. Once the initial token is fetched there will should
	// never be a time where we don't have a token, so this won't be called anymore.
	if (!token) {
		return new Promise((resolve, reject) => {
			const token = useIdentityStore.getState().token;

			if (token) {
				return resolve(token);
			}

			const timeout = setTimeout(() => {
				unsubscribe();
				reject(new HttpError(401));
			}, 20_000);

			const unsubscribe = useIdentityStore.subscribe((state) => {
				const token = state.token;

				if (token) {
					unsubscribe();
					clearTimeout(timeout);
					resolve(token);
				}
			});
		});
	}

	// If there is no token or an old token without an expiration date, return it. The server will
	// return a 401 if the token is invalid/absent and the user will be redirected to the login page.
	if (!isExpired(token)) {
		debugLog('returning existing token or undefined');
		return Promise.resolve(token);
	}
	debugLog('token expired, calling refreshToken');
	return await refreshToken();
};

export const fetchToken = async (): Promise<string | undefined> => {
	const application = useApplication.getState().application;
	const csrfToken = Cookies.get(CSRF_COOKIE_ID);
	const url = joinPath(application.recognizeOrigin, 'api/v2/jwt');

	try {
		const res = await fetch(url, {
			credentials: 'include',
			headers: {
				'X-BONUSLY-SOURCE': 'internal',
				...(csrfToken && {
					[CSRF_COOKIE_ID]: csrfToken,
				}),
			},
		});

		// If unauthorized, clear any stored JWT
		if (res.status === 401) {
			debugLog('fetchToken received 401, clearing stored token');
			clearTokenFromStorage();
			return undefined;
		}

		const json = await res.json();

		// CRITICAL: Do NOT save session-based JWTs to cookie
		// Session JWTs can change (spoofing, logout, etc.)
		// Only URL-based JWTs are cached (see getTokenFromUrl and widget-page.tsx postMessage)
		// This ensures spoofing state changes are immediately reflected
		//
		// Manual Testing Required (difficult to automate):
		// 1. Spoof user → navigate to /widget → stop spoof → refresh → verify correct user
		// 2. See docs/development/widget-testing.md for complete spoofing test scenarios

		return json.jwt;
	} catch (error) {
		debugLog('fetchToken error', error);
		// On error, don't clear cookie - it might be a network issue
		return undefined;
	}
};

export const refreshToken = async (): Promise<string | undefined> => {
	// First check if we have a cached URL JWT to use
	if (urlJwtCache) {
		useIdentityStore.setState({ token: urlJwtCache });
		const token = urlJwtCache;
		urlJwtCache = undefined; // Clear cache after use
		// JWT from URL is already saved to cookie by getTokenFromUrl
		return token;
	}

	// Check if we have a persisted JWT in cookie (for iframe reload)
	// CRITICAL: Only use cached JWT if we're actually in an iframe
	// This prevents cached JWTs from interfering with session-based auth (direct navigation, spoofing)
	//
	// Example: Superadmin tests widget in iframe → JWT cached → later spoofs user in main window
	// Without iframe check: Would use cached superadmin JWT instead of spoofed user's session
	// With iframe check: Main window fetches fresh JWT from session ✓
	//
	// Manual Testing Required (difficult to automate):
	// - Widget iframe reload: Authenticate in iframe → navigate away → return → verify still authenticated
	// - See docs/development/widget-testing.md for iframe authentication test scenarios

	if (isInIframe()) {
		const storedToken = getTokenFromStorage();
		if (storedToken) {
			// Validate the stored token
			if (!isExpired(storedToken)) {
				debugLog('Using persisted cookie on refresh (iframe context)');
				useIdentityStore.setState({ token: storedToken });
				return storedToken;
			}
			// Clear expired cookie
			debugLog('Persisted cookie expired, clearing');
			clearTokenFromStorage();
		}
	} else {
		// Not in iframe - direct navigation or main window
		// Always fetch fresh JWT from session to respect current auth state (including spoofing)
		debugLog('Not in iframe context, skipping cookie check');
	}

	// Fallback to normal JWT fetching via API
	// CRITICAL: fetchToken() does NOT cache session-based JWTs
	// This ensures spoofing state changes are immediately reflected
	const jwt = await fetchToken();

	useIdentityStore.setState({ token: jwt });

	return jwt;
};
